import { create } from 'zustand';

export type ToastKind = 'info' | 'ok' | 'err';

export interface Toast {
  id: number;
  text: string;
  kind: ToastKind;
  /**
   * Сповіщення вже йде. Воно ще в списку, але малюється анімацією зникнення —
   * інакше зникало б різко: елемент, якого немає в стані, не має чим зникати.
   */
  leaving?: boolean;
}

type Theme = 'light' | 'dark';

export type ThemeOrigin = { x: number; y: number };

interface UiState {
  theme: Theme;
  toasts: Toast[];
  /**
   * Меню телефона відкрите. Стан спільний, бо кнопка «☰» стоїть у шапці —
   * одна на всі сторінки, — а вміст меню в таблиці свій: там до нього
   * додаються «Вигляд» і «Дії». Тож натискає одна сторінка, а малює інша.
   */
  menu: boolean;
  setMenu: (open: boolean) => void;
  /** Вікно «Оновлення»: відкривають і зі шапки, і з меню телефона. */
  updates: boolean;
  setUpdates: (open: boolean) => void;
  setTheme: (t: Theme, origin?: ThemeOrigin) => void;
  toast: (text: string, kind?: ToastKind) => void;
  dismiss: (id: number) => void;
}

/**
 * Колір смуги браузера — з теми застосунку.
 *
 * У html лежала константа, і вона не збігалася ні зі світлою шапкою, ні з
 * темною, ні з кольорами курсу. Беремо `--panel` — те саме, чим пофарбована
 * шапка, — уже обчисленим, тож і накладені палітри враховані.
 */
export const syncThemeColor = (tries = 0): void => {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const panel = getComputedStyle(document.documentElement).getPropertyValue('--panel').trim();
  if (panel) {
    meta.setAttribute('content', panel);
    return;
  }
  // Стилі підключаються скриптом, тож на першому кадрі токена може ще не бути.
  if (tries < 10) requestAnimationFrame(() => syncThemeColor(tries + 1));
};

const applyTheme = (theme: Theme): void => {
  document.documentElement.dataset.theme = theme;
  // Кадром пізніше: перемальовані змінні читаються вже застосованими.
  requestAnimationFrame(() => syncThemeColor());
};

/** Точка, з якої розходиться коло теми: клік або центр кнопки (клавіатура). */
export const themeOriginFromClick = (e: {
  clientX: number;
  clientY: number;
  currentTarget: EventTarget & Element;
}): ThemeOrigin => {
  if (e.clientX !== 0 || e.clientY !== 0) return { x: e.clientX, y: e.clientY };
  const r = e.currentTarget.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
};

/** Було «auto» / порожньо — фіксуємо раз від системи, далі лише light|dark. */
const readStored = (): Theme => {
  const v = localStorage.getItem('kanc.theme');
  if (v === 'light' || v === 'dark') return v;
  const theme: Theme = matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  localStorage.setItem('kanc.theme', theme);
  return theme;
};

const stored = readStored();
applyTheme(stored);

let seq = 0;
let themeBusy = false;

/** Скільки триває анімація зникнення — має збігатися з CSS. */
const TOAST_EXIT = 220;

const commitTheme = (theme: Theme, set: (p: Partial<UiState>) => void): void => {
  localStorage.setItem('kanc.theme', theme);
  applyTheme(theme);
  set({ theme });
};

export const useUi = create<UiState>((set, get) => ({
  theme: stored,
  toasts: [],
  menu: false,
  setMenu: menu => set({ menu }),
  updates: false,
  setUpdates: updates => set({ updates }),
  setTheme: (theme, origin) => {
    if (theme === get().theme || themeBusy) return;

    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canVt = typeof document.startViewTransition === 'function';

    if (!canVt || reduced || !origin) {
      commitTheme(theme, set);
      return;
    }

    const { x, y } = origin;
    const r = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
    const root = document.documentElement;
    root.style.setProperty('--theme-x', `${x}px`);
    root.style.setProperty('--theme-y', `${y}px`);
    root.style.setProperty('--theme-r', `${r}px`);

    themeBusy = true;
    root.classList.add('theme-vt');
    const transition = document.startViewTransition(() => commitTheme(theme, set));
    void transition.finished.finally(() => {
      themeBusy = false;
      root.classList.remove('theme-vt');
      root.style.removeProperty('--theme-x');
      root.style.removeProperty('--theme-y');
      root.style.removeProperty('--theme-r');
    });
  },
  toast: (text, kind = 'info') => {
    const id = ++seq;
    // Помилку читають довше, ніж «Збережено».
    const life = kind === 'err' ? 6000 : 3000;
    set(s => ({ toasts: [...s.toasts, { id, text, kind }] }));
    setTimeout(() => useUi.getState().dismiss(id), life);
  },
  /**
   * Спершу позначаємо, що йде, і лише за час анімації прибираємо зі стану.
   * Тому повторний виклик на те саме сповіщення нічого не робить: воно вже
   * прощається, і другий таймер тільки збив би анімацію.
   */
  dismiss: id =>
    set(s => {
      const t = s.toasts.find(x => x.id === id);
      if (!t || t.leaving) return s;
      setTimeout(() => set(st => ({ toasts: st.toasts.filter(x => x.id !== id) })), TOAST_EXIT);
      return { toasts: s.toasts.map(x => (x.id === id ? { ...x, leaving: true } : x)) };
    }),
}));

export const toast = (text: string, kind: ToastKind = 'info'): void => useUi.getState().toast(text, kind);
